import type { JSX } from 'react';

import { Text } from '@change/ds-core-ui';
import { CheckIcon, LinkIcon } from '@change/ds-icons';
import { tv } from '@change/ds-utils';

import { Button } from '@change/ds-core-ui/next';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { Translate } from '@change-corgi/core/react/i18n';

import type { ShareChannel } from 'src/app/shared/hooks/share';

import { useCopyLinkVisibleUrlButton } from './hooks/useCopyLinkVisibleUrlButton';

type SupportedShareChannels = Exclude<ShareChannel, 'qrcode' | 'flyer'>;
type ShareButtonProps = Readonly<{
	action: () => Promise<boolean> | Promise<void>;
	channel: SupportedShareChannels;
	label: TranslationKey;
	successLabel?: TranslationKey;
	url: string;
	urlDescription?: TranslationKey;
}> &
	React.ComponentPropsWithoutRef<'div'>;

export function CopyLinkVisibleUrlButton({
	action,
	channel,
	label,
	successLabel,
	url,
	urlDescription,
	...rest
}: ShareButtonProps): JSX.Element {
	const { data, actions } = useCopyLinkVisibleUrlButton({ action, label, successLabel, url });

	const buttonClass = tv({
		base: 'flex border-2 border-black',
		variants: {
			clicked: {
				true: 'text-typography-success',
				false: 'icon-strong-fixed',
			},
		},
	});

	return (
		<div className="elevation-low rounded-large border-stroke-weaker flex flex-col gap-3 border px-5 py-4" {...rest}>
			<div className="bg-fill-weaker rounded-lg px-7 py-3 text-xs">
				<div className="text-typography-strong leading-5">
					{urlDescription && <Translate value={urlDescription}></Translate>}
				</div>
				<div className="text-typography-strong select-none overflow-hidden text-ellipsis whitespace-nowrap text-xs font-bold leading-6">
					{data.resolvedUrl}
				</div>
			</div>

			<Button
				leadingIcon={
					data.clickedState ? <CheckIcon data-qa="copied-icon-check" className="text-icon-success" /> : <LinkIcon />
				}
				onClick={actions.share}
				variant="primary"
				disabled={data.clickedState}
				className={buttonClass({ clicked: data.clickedState })}
			>
				<Text size="medium" fontWeight="bold" className="flex gap-1.5" color="bg-red-500">
					<Translate value={data.buttonLabel} />
				</Text>
			</Button>
		</div>
	);
}
